import empty from "is-empty";

import { SettingsSelectors } from "src/redux/Settings/selectors";
import { Inventory } from "./types";
import { FETCH_STATES } from "src/redux/Tx/constants";
import { MD } from "src/redux/MarketData/types";
import {
    qtyByCountry,
    groupBy,
    getDateFromTerm,
    getTermFromDate,
    getClampedMaturityDate,
} from "src/utils";
import Math from "src/utils/math";
import { UPLOAD_INVENTORY_MODES } from "./constants";

const defaultState = {
    uploadRows: [],
    uploadMode: null,
    todayInventory: {},
    // rows which have already been edited (there's differences)
    editedRows: {},
    // rows which have been selected for editing (may not be any diff yet)
    editingRowIDs: new Set(),
    selected: [],
    error: null,
};

const updateInventory = (inventory, updates, updater) => {
    const newObj = {};
    const inventoryKeyList = updates.map((i) =>
        [i.security.ticker, i.listName].join(",")
    );
    Object.keys(inventory).forEach((e) => {
        newObj[e] = inventory[e].map((i) => {
            const idx = inventoryKeyList.indexOf(
                [i.security.ticker, i.listName].join(",")
            );
            if (idx >= 0) {
                return updater(i, updates[idx]);
            } else {
                return i;
            }
        });
    });
    return newObj;
};

export default (state = defaultState, action) => {
    switch (action.type) {
        case Inventory.SET_UPLOAD_ENTRIES:
            return {
                ...state,
                uploadRows: action.entries.map((data, index) => ({
                    ...data,
                    id: index + 1,
                })),
            };

        case Inventory.SET_UPLOAD_MODE:
            return {
                ...state,
                uploadMode: action.mode,
            };

        case Inventory.MARKET_DATA_BATCH_UPDATE:
            let hasMarkitAccess = false;
            let newEntries = state.uploadRows.map((row, index) => {
                const stockSymbol = row.SecurityId;
                const batchStockData = action.result;
                if (!action.listSecurityId.includes(stockSymbol)) return row;

                let stockItem = batchStockData[stockSymbol]
                    ? batchStockData[stockSymbol].data
                    : null;
                if (index === 0) {
                    hasMarkitAccess = !empty(stockItem?.referenceRate);
                }
                if (stockItem) {
                    const benchmarkFee = stockItem.referenceRate?.markit?.value;
                    const quantity = qtyByCountry(
                        row.MyQuantity,
                        stockItem.countryCode,
                        false
                    );
                    const TradeableDates = stockItem.tradableDates.map((s) =>
                        s.replace(/-/g, "")
                    );
                    return {
                        ...row,
                        marketData: {
                            status: FETCH_STATES.FETCHED,
                            data: stockItem,
                            error: null,
                        },
                        security: stockItem,
                        hasError: false,
                        Name: stockItem.name,
                        BbgCode: stockItem.bbgCode,
                        LocalName: stockItem.localName,
                        PreviousClose: stockItem.closingPrice,
                        Amount: stockItem.closingPrice * quantity,
                        Country: stockItem.countryCode,
                        TradeableDates,
                        MyQuantity: quantity,
                        BenchmarkFee: benchmarkFee,
                        CalculatedRate: row.MyRate,
                        Term: getTermFromDate(row.ValueDate, row.Maturity),
                        maxMaturityDates: stockItem.maxMaturityDates,
                    };
                }

                return {
                    ...row,
                    marketData: {
                        status: FETCH_STATES.ERROR,
                        error: "Not Found",
                    },
                    SecurityId: row.SecurityId + " ",
                    hasError: true,
                    Name: "ERROR",
                    LocalName: "ERROR",
                    PreviousClose: "ERROR",
                    Amount: "ERROR",
                    Country: "ERROR",
                };
            });

            return {
                ...state,
                hasMarkitAccess,
                uploadRows: newEntries,
            };

        case Inventory.MARK_REPLACING_UPLOAD_ROW:
            const uploadMode = state.uploadMode;
            if (uploadMode === UPLOAD_INVENTORY_MODES.REPLACE) return state;

            const inventoryKeyList = Object.values(state.todayInventory)
                .flat()
                .map((i) => [i.security.ticker, i.listName].join(","));
            return {
                ...state,
                uploadRows: state.uploadRows.map((row) => {
                    if (row.hasError) return { ...row, isReplacing: false };
                    const rowKey = [row.security.ticker, row.ListName].join(
                        ","
                    );

                    return {
                        ...row,
                        isReplacing: inventoryKeyList.includes(rowKey),
                    };
                }),
            };

        case Inventory.NEW_INVENTORY_LIST:
            const invenToAdd = Array.isArray(action.inventory)
                ? groupBy(
                      action.inventory.map((i) => ({
                          ...i,
                          ticker: i.security.ticker,
                      })),
                      "ticker"
                  )
                : {};

            const keys = Object.keys(invenToAdd);
            const todayInventory = { ...state.todayInventory };
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                if (todayInventory[key]) {
                    const listNames = invenToAdd[key].map((i) => i.listName);
                    todayInventory[key] = todayInventory[key]
                        .filter((i) => !listNames.includes(i.listName))
                        .concat(invenToAdd[key]);
                } else {
                    todayInventory[key] = invenToAdd[key];
                }
            }
            return {
                ...state,
                todayInventory,
            };

        case Inventory.REMOVE_INVENTORY_LIST:
            const newInventory = { ...state.todayInventory };
            if (!empty(action.inventory)) {
                const invenToRemove = groupBy(
                    action.inventory.map((i) => ({
                        ...i,
                        ticker: i.security.ticker,
                    })),
                    "ticker"
                );
                Object.keys(invenToRemove).forEach((key) => {
                    const listNames = invenToRemove[key].map((i) => i.listName);
                    newInventory[key] = newInventory[key].filter(
                        (i) => !listNames.includes(i.listName)
                    );
                });
            }

            return {
                ...state,
                todayInventory: newInventory,
            };

        case Inventory.UPDATE_INVENTORY_LIST:
            return {
                ...state,
                todayInventory: updateInventory(
                    state.todayInventory,
                    action.inventory,
                    (prevInventory, newInventory) => ({
                        ...prevInventory,
                        ...newInventory,
                        // Must always track this, even if set to null
                        lastUser: newInventory.lastUser,
                    })
                ),
            };

        case Inventory.START_ROW_EDIT:
            return {
                ...state,
                editingRowIDs: new Set([...state.editingRowIDs, action.rowId]),
                editedRows: {
                    ...state.editedRows,
                    [action.rowId]: {}, // start tracking changes
                },
            };

        case Inventory.CANCEL_ROW_EDIT:
            const { [action.rowId]: _, ...restEdited } = state.editedRows;
            return {
                ...state,
                editingRowIDs: new Set(
                    [...state.editingRowIDs].filter((id) => id !== action.rowId)
                ),
                editedRows: restEdited,
            };

        case Inventory.APPLY_ROW_EDIT: {
            const newTodayInventory = Object.fromEntries(
                Object.entries(state.todayInventory).map(([ticker, rows]) => [
                    ticker,
                    rows.map((row) => {
                        const rowId = `${row.listName}-${row.security.isin}`;

                        if (rowId === action.rowId) {
                            const editedValues = state.editedRows[rowId] || {};

                            // todo: can we unify these names?
                            return {
                                ...row,
                                uploadedQuantity:
                                    editedValues.UploadedQuantity ??
                                    row.uploadedQuantity,
                                valueDate:
                                    editedValues.ValueDate !== undefined
                                        ? editedValues.ValueDate.replace(
                                              /\//g,
                                              "-"
                                          )
                                        : row.valueDate,
                                preferredRate:
                                    editedValues.PreferredRate ??
                                    row.preferredRate,
                                intermediary:
                                    editedValues.Intermediary ??
                                    row.intermediary,
                            };
                        } else {
                            return row;
                        }
                    }),
                ])
            );

            return {
                ...state,
                todayInventory: newTodayInventory,
                editingRowIDs: new Set(
                    [...state.editingRowIDs].filter((id) => id !== action.rowId)
                ),
                editedRows: Object.fromEntries(
                    Object.entries(state.editedRows).filter(
                        ([id]) => id !== action.rowId
                    )
                ),
            };
        }

        case Inventory.SET_EDITED_FIELD: {
            const { rowId, field, value } = action;

            return {
                ...state,
                editedRows: {
                    ...state.editedRows,
                    [rowId]: {
                        ...state.editedRows[rowId],
                        [field]: value,
                    },
                },
            };
        }

        case Inventory.DELETE_UPLOAD_ROW:
            let newDeletedRows = [...state.uploadRows].filter(
                (r) => r.id !== action.rowId
            );
            return {
                ...state,
                uploadRows: newDeletedRows.map((row, idx) => ({
                    ...row,
                    id: idx + 1,
                })),
            };
        case Inventory.UPDATE_UPLOAD_ROW:
            const ids = action.rows.map((r) => r.id);
            return {
                ...state,
                uploadRows: state.uploadRows.map((r) => {
                    const position = ids.indexOf(r.id);
                    if (position >= 0) return action.rows[position];
                    return r;
                }),
            };
        case MD.MARKET_DATA_UPDATE:
            let mdRowData = state.uploadRows.map((row) => {
                if (row.SecurityId === action.stockSymbol) {
                    const quantity = qtyByCountry(
                        row.MyQuantity,
                        action.result.data.countryCode,
                        false
                    );
                    return {
                        ...row,
                        marketData: {
                            status: FETCH_STATES.FETCHED,
                            data: action.result.data,
                            id: action.result.id,
                            error: null,
                        },
                        secuirty: action.result.data,
                        Name: action.result.data.name,
                        LocalName: action.result.data.localName,
                        PreviousClose: action.result.data.closingPrice,
                        Amount: action.result.data.closingPrice * quantity,
                        Country: action.result.data.countryCode,
                        TradeableDates: action.result.data.tradableDates.map(
                            (s) => s.replace(/-/g, "")
                        ),
                        MyQuantity: quantity,
                    };
                } else {
                    return row;
                }
            });
            return {
                ...state,
                uploadRows: mdRowData,
            };

        case Inventory.APPLY_REF_RATE:
            return {
                ...state,
                uploadRows: state.uploadRows.map((d) => ({
                    ...d,
                    CalculatedRate: d.BenchmarkFee
                        ? Math.chain(d.BenchmarkFee)
                              .divide(100)
                              .multiply(action.multiplier)
                              .done()
                        : d.MyRate,
                })),
            };

        case Inventory.APPLY_MATURITY:
            const {
                "enable-90-day-max-maturity-date": enable90DayMaxMaturity,
            } = SettingsSelectors.FeatureFlags(state);

            return {
                ...state,
                uploadRows: state.uploadRows.map((d) => {
                    if (action.maturity.type === "MAX") {
                        return {
                            ...d,
                            Term: getTermFromDate(
                                d.ValueDate,
                                getClampedMaturityDate(
                                    d.Country,
                                    d.Purpose,
                                    d.Maturity,
                                    d.ValueDate,
                                    d.TradeableDates,
                                    d.maxMaturityDates,
                                    enable90DayMaxMaturity
                                )
                            ),
                        };
                    }

                    return {
                        ...d,
                        Maturity: d.ValueDate
                            ? action.maturity.date ||
                              getDateFromTerm(d.ValueDate, action.maturity.term)
                            : new Date("9999-12-31"),
                        Term: d.ValueDate
                            ? action.maturity.term ||
                              getTermFromDate(d.ValueDate, action.maturity.date)
                            : "",
                    };
                }),
            };

        case Inventory.APPLY_INTERMEDIARY:
            return {
                ...state,
                uploadRows: state.uploadRows.map((d) => ({
                    ...d,
                    intermediary: action.intermediary,
                })),
            };

        case MD.MD_ERROR:
            let mdRowDataWithError = state.uploadRows.map((row) => {
                if (row.SecurityId === action.stockSymbol) {
                    return {
                        ...row,
                        marketData: {
                            status: FETCH_STATES.ERROR,
                            error: "Not Found",
                        },
                        SecurityId: row.SecurityId + " ",
                        hasError: true,
                        Name: "ERROR",
                        LocalName: "ERROR",
                        PreviousClose: "ERROR",
                        Amount: "ERROR",
                        Country: "ERROR",
                    };
                } else {
                    return row;
                }
            });
            return {
                ...state,
                uploadRows: mdRowDataWithError,
            };

        case Inventory.ERROR:
            return {
                ...state,
                error: action.error,
            };

        case Inventory.SET_SELECTED_INVENTORY:
            // NOTE: prevent infinite firing of ag-grid onRowDataUpdated event
            if (JSON.stringify(state.selected) === JSON.stringify(action.rows))
                return state;
            return {
                ...state,
                selected: [...action.rows],
            };

        default:
            return state;
    }
};
